import React, {useReducer} from "react";

import PackHeader from "./pack-header";
import PackOffer from "./pack-offer";
import PackVariableWeight from "./pack-variable-weight";
import PackConstantWeight from "./pack-constant-weight";
// utility functions (you know, for making the code below pretty and clutter less)
import {
  packReducer,
  eventAttrBuilder,
  weightOrder,
  alsoImportantWeights,
} from "./utility";

const Pack = (props) => {
  // restructured the variates prop (it tells me what weights are variable in this pack)
  const {variates = []} = props;

  // this is the current state variable / setter | it's a key->value store with this format:
  // weightKey -> selectedWeightIdForWeightKey
  // it starts off with the first entry OR the first entry with a default prop as true
  // read fantastic reducer below
  const [selectedState, dispatch] = useReducer(
    packReducer,
    variates.reduce((acc, item) => {
      return Object.assign({}, acc, {
        [item]: props.weights[item].reduce(
          (_acc, _item) => (_item.default ? _item.id : _acc),
          props.weights[item][0].id
        ),
      });
    }, {})
  );

  // idInternal used for iSell model events
  const [selectedIdInternal, dispatchIdInternal] = useReducer(
    packReducer,
    variates.reduce((acc, item) => {
      return Object.assign({}, acc, {
        [item]: props.weights[item].reduce(
          (_acc, _item) => (_item.default ? _item.idInternal : _acc),
          props.weights[item][0].idInternal
        ),
      });
    }, {})
  );

  // another utility function to get the selected option from a given weight key
  // it's not very useful outside of this context so it's not inside of the utility file
  // deal with it or take it out, your choice

  const getSelectedWeight = (weights, weightId) =>
    weights.filter((weight) => weight.id == weightId)[0];

  // this is another one of those amazing reducers
  // what this does is:
  //  1 - assigns to an empty object the initial weights on the pack (JSON)
  //  2 - reduces the variates variable (you know the one that holds the keys of the weights that change)
  //  3 - gets the selected weight option and checks for weightModifiers (the stuff that modifies other weights)
  //  4 - if there is a weightModifiers present then assign its value to the accumulator (overriding the initial weights)
  //  5 - if not then just keep going
  // why is this cool? because it allows for a 1-level override on the weights based on the selected weight options
  // frikin amazing! this little thing right here is what does most of the magic...
  const currentWeights = Object.assign(
    {},
    props.weights,
    variates.reduce((acc, item) => {
      let weights = props.weights[item];

      // verificar se o serviço do Isel currentWeights[item][0].weightModifiers.KEY é um []
      // se for [] e só tem um obj, transformar num obj{}
      let itemWeightModifier = weights[0].weightModifiers || null;
      //itemWeightModifier[Object.keys(itemWeightModifier)[0]]

      if (
        itemWeightModifier &&
        itemWeightModifier[Object.keys(itemWeightModifier)[0]].length === 1 &&
        Array.isArray(itemWeightModifier[Object.keys(itemWeightModifier)[0]])
      ) {
        // console.log(Array.isArray(itemWeightModifier[Object.keys(itemWeightModifier)[0]]));
        // console.log( itemWeightModifier[Object.keys(itemWeightModifier)[0]].length );
        weights[0].weightModifiers[Object.keys(weights[0].weightModifiers)[0]] =
          itemWeightModifier[Object.keys(itemWeightModifier)[0]][0];
      }

      const selectedWeight = getSelectedWeight(
        props.weights[item],
        selectedState[item]
      );

      return selectedWeight.hasOwnProperty("weightModifiers")
        ? Object.assign(acc, selectedWeight.weightModifiers)
        : acc;
    }, {})
  );

  // another new functionality
  // basically it iterates through the current weights' selected options and checks for one that alters the visibility of the weights
  // this does not remove the weights from the state, just hides them
  // this solution came to solve the problem where you may need more visible weigths as you select other options:
  // e.g.:
  // weight W - option A must show weigths X and Y
  // weight W - option B must show weights X Y and Z
  // this is achieved by setting the weightVisibility on option A to have only the keys of W X and Y
  // and by setting the weightVisibility on option B to have only the keys of W X Y and Z
  // ANY WEIGHTVISIBILITY MODIFIER MUST CONTAIN THE OWN WEIGHT AS KEY (it doesn't make much sense otherwise)
  // IF NO WEIGHT VISIBILITY IS PROVIDED THEN EVERY WEIGHT IS VISIBLE
  // IF AT LEAST ONE WEIGHT VISIBILITY KEY IS PRESENT THEN ONLY THAT KEY IS VISIBLE

  const currentWeightVisibility = variates.reduce(
    (acc, item) => {
      // console.log(currentWeights[item])
      const selectedWeight = getSelectedWeight(
        currentWeights[item],
        selectedState[item]
      );

      return selectedWeight.hasOwnProperty("weightVisibility")
        ? acc.concat(selectedWeight.weightVisibility)
        : acc;
    },
    props.hasOwnProperty("weightVisibility") ? props.weightVisibility : []
  );

  const visibleVariates = variates.filter(
    (i) =>
      currentWeightVisibility.length == 0 ||
      currentWeightVisibility.indexOf(i) != -1
  );

  // well after the first magic bit there's really no surprise in this one
  // it's the same thing as the one above (weightModifiers) but for packModifiers
  // now here's a bit of wise knowledge
  // DO NOT (I repeat) DO NOT make a packModifiers the uses the "weights" key or the "variates" key
  // it will not work
  // it may break stuff
  // it's like dividing by 0
  // simply put, don't do it
  // it follows the design principle that says "Garbage in - Garbage out"
  // try to throw garbage JSON in, you know the rest...
  const currentPack = Object.assign(
    {
      offer: null,
      title: "",
      price: 0,
      oldPrice: null,
      url: "",
      promotion: null,
      event: {},
    },
    props,
    visibleVariates.reduce((acc, item) => {
      const selectedWeight = getSelectedWeight(
        currentWeights[item],
        selectedState[item]
      );
      return selectedWeight.hasOwnProperty("packModifiers")
        ? Object.assign(acc, selectedWeight.packModifiers)
        : acc;
    }, {})
  );

  // this one is a little different
  // you see, priceDelta is the sum of all the selected weigths "priceModifier"
  // but not only that it also goes through every "constant" weight and gets its priceModifier (because yes)
  // although it makes little sense to programatically iterate the "constants" it's more of a nice-to-have bit
  // this way the data can be aggregated by weight and follows more closely the business model logic

  // const currentPriceDelta = variates.reduce((acc, item) => {
  // 	const selectedWeight = getSelectedWeight(currentWeights[item], selectedState[item]);
  // 	return selectedWeight.hasOwnProperty("priceModifier") ? acc + selectedWeight.priceModifier : acc
  // }, 0) + weightOrder.concat(alsoImportantWeights).filter((w) => currentWeights.hasOwnProperty(w) && variates.indexOf(w) == -1).reduce((acc, weight) => {
  // 	return acc + (currentWeights[weight].hasOwnProperty("priceModifier") ? currentWeights[weight].priceModifier : 0)
  // }, 0)

  // would you look at that!
  // it's the same thing but with a different key!
  // imma make a function out of it!

  // const currentDiscount = variates.reduce((acc, item) => {
  // 	const selectedWeight = getSelectedWeight(currentWeights[item], selectedState[item]);
  // 	return selectedWeight.hasOwnProperty("discount") ? acc + selectedWeight.discount : acc
  // }, 0) + weightOrder.concat(alsoImportantWeights).filter((w) => currentWeights.hasOwnProperty(w) && variates.indexOf(w) == -1).reduce((acc, weight) => {
  // 	return acc + (currentWeights[weight].hasOwnProperty("discount") ? currentWeights[weight].discount : 0)
  // }, 0)

  // done!
  // it receives a key and iterates the selected weights and the constant weights
  // gets the property key and adds it to the accumulator
  const accumulateWeightProperty = (weightPropKey) =>
    visibleVariates.reduce((acc, item) => {
      const selectedWeight = getSelectedWeight(
        currentWeights[item],
        selectedState[item]
      );
      return selectedWeight.hasOwnProperty(weightPropKey)
        ? acc + selectedWeight[weightPropKey]
        : acc;
    }, 0) +
    weightOrder
      .concat(alsoImportantWeights)
      .filter(
        (w) =>
          currentWeights.hasOwnProperty(w) && visibleVariates.indexOf(w) == -1
      )
      .reduce((acc, weight) => {
        return (
          acc +
          (currentWeights[weight].hasOwnProperty(weightPropKey)
            ? currentWeights[weight][weightPropKey]
            : 0)
        );
      }, 0);

  // much better (being lazy pays off)
  const currentPriceDelta = accumulateWeightProperty("priceModifier");
  const currentDiscount = accumulateWeightProperty("discount");

  const getCurrentDiscountText = (weightPropKey) =>
    visibleVariates.reduce((acc, item) => {
      const selectedWeight = getSelectedWeight(
        currentWeights[item],
        selectedState[item]
      );
      return selectedWeight.hasOwnProperty(weightPropKey)
        ? acc + selectedWeight[weightPropKey]
        : acc;
    }, "");

  const currentDiscountText = getCurrentDiscountText("discountText");

  // showoff
  // const [currentPriceDelta, currentDiscount] = ["priceModifier", "discount"].map(accumulateWeightProperty)

  // for change events dispatch a SET action with the values it needs to change
  // this is what changes the selectedState variable
  const handleChange = (weightType, valueId, valueIdInternal) => (e) => {
    dispatch({
      type: "SET",
      payload: {
        type: weightType,
        value: valueId,
      },
    });

    dispatchIdInternal({
      type: "SET",
      payload: {
        type: weightType,
        value: valueIdInternal,
      },
    });
  };

  // this bit right here is new stuff
  // what it does is look for replace patterns in the URL prop ( you know, stuff like this {{NAME}} )
  // once it finds the replace pattern (one for each possible weight) it replaces it with the weight's selected option's ID
  // example:
  // currentPack.url = "example.com/whatever?TV={{tv}}&OFFER={{offer}}"
  // if the pack has both TV and OFFER it will replace the patterns for their ID (let's assume you have BASIC as TV and NETFLIX as offer)
  // output = "example.com/whatever?TV=BASIC&OFFER=NETFLIX"
  // so you can change the pack endpoint parameters based on the selected options

  // console.log("before url", currentWeights)

  const currentUrl = currentPack.url
    ? weightOrder.concat(alsoImportantWeights).reduce(
        (acc, item) =>
          acc.replace(
            new RegExp("{{" + item + "}}", "g"),
            // selectedIdInternal[item] ||	//para fazer refresh a todos os campos cada vez que actualiza o url
            // selectedState[item] ||	//para fazer refresh a todos os campos cada vez que actualiza o url
            currentWeights[item]
              ? // [] caso o weight seja um só e seja array
                currentWeights[item].idInternal ||
                (currentWeights[item][0] &&
                  currentWeights[item].filter(
                    (weight) => weight.id == selectedState[item]
                  )[0])
                ? currentWeights[item][0] !== undefined
                  ? currentWeights[item].filter(
                      (weight) => weight.id == selectedState[item]
                    )[0].idInternal
                  : currentWeights[item].idInternal
                : // 	currentWeights[item].idInternal ||
                  // currentWeights[item][0] !== undefined && currentWeights[item].filter((weight) => weight.id == selectedState[item])[0] ? currentWeights[item].filter((weight) => weight.id == selectedState[item])[0].idInternal : currentWeights[item][0].idInternal

                  // currentWeights[item][0].idInternal
                  currentWeights[item].idInternal
              : //	||
                //   currentWeights[item].id ||
                //   currentWeights[item][0].id
                ""
          ),

        currentPack.url
      )
    : "";
  // console.log("ENV", window);
  // console.log("ENV", process);
  // console.log("currentUrl", currentUrl);
  // console.log("currentPack", currentPack);
  // getSelectedWeight(currentWeights[item], selectedState[item]) ? getSelectedWeight(currentWeights[item], selectedState[item]).idInternal :
  // console.log(selectedState, currentWeights)
  // another good bit here
  // we put everything together here
  // PackHeader - header with price and button and texts
  // PackOffer - offers (it's a weight as well but it goes on a different part of the DOM and it looks different)
  // PackVariableWeight - for weights that can vary
  // PackConstantWeigth - for weights that are constant
  console.log({currentPack});
  return (
    <li
      className={
        "pack-item " +
        (currentPack.offer ? currentPack.offer.color : "") +
        (props.resume ? " block-collapse" : "")
      }
    >
      <PackHeader
        resume={props.resume}
        showTitle={currentPack.showTitle}
        title={currentPack.title}
        label={currentPack.label}
        billingCycle={currentPack.billingCycle}
        url={currentUrl}
        urlCustom={currentPack.urlCustom}
        urlText={currentPack.urlText}
        UrlTriggerEventCaution={props.UrlTriggerEventCaution}
        price={currentPack.price + currentPriceDelta - currentDiscount}
        oldPrice={currentPack.oldPrice}
        discount={currentDiscount}
        discountText={currentDiscountText}
        event={eventAttrBuilder(
          props.idx + 1,
          currentPack.event.region,
          currentPack.title,
          1,
          currentPack.event.link,
          "Aderir"
        )}
        promotion={
          currentPack.promotion
            ? {
                text: currentPack.promotion.text,
                months: currentPack.promotion.months,
                priceAfter:
                  currentPack.promotion.priceAfter + currentPriceDelta,
              }
            : null
        }
      />
      <div
        className={"pack-body " + (props.resume ? " collapse-content off" : "")}
      >
        <div className="wrp">
          <PackOffer
            currentPack={currentPack}
            currentWeights={currentWeights}
            packIdx={props.idx}
            selectedState={selectedState}
            handleChange={handleChange}
            resume={props.resume}
          />
          <div className="pack-details">
            <ul>
              {weightOrder
                .filter((i) => currentWeights.hasOwnProperty(i)) // only existing weights
                .filter(
                  (i) =>
                    currentWeightVisibility.length == 0 ||
                    currentWeightVisibility.indexOf(i) != -1
                ) // only visible weights if there is weight visibility rules present
                .map((item) => {
                  if (
                    !currentWeights[item].length ||
                    (currentWeights[item].length == 1 &&
                      visibleVariates.indexOf(item) == -1)
                  ) {
                    // if it has no length or it has only 1 option and does not variate then it's a constant weight
                    // since it is a constant weight render the appropriate component
                    return (
                      <PackConstantWeight
                        item={item}
                        currentWeights={currentWeights}
                      />
                    );
                  } else {
                    // if the weight has length then it has multiple options
                    // if it has multiple options then its a variable weight
                    // then render the appropriate component
                    return (
                      <PackVariableWeight
                        getSelectedWeight={getSelectedWeight}
                        currentWeights={currentWeights}
                        currentPack={currentPack}
                        packIdx={props.idx}
                        item={item}
                        handleChange={handleChange}
                        selectedState={selectedState}
                      />
                    );
                  }
                })}
            </ul>
          </div>
        </div>
      </div>
    </li>
  );
};

export default Pack;
